'use client';

import { useEffect } from 'react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLenis } from '@/contexts/LenisContext';
import { gsap } from '@/lib/gsap-config';

gsap.registerPlugin(ScrollTrigger);

export function GsapLenisConnector() {
  const { lenis } = useLenis();

  useEffect(() => {
    if (!lenis) return;

    const docEl = document.documentElement;

    // Clear any existing RAF animations if they exist
    lenis.stop();

    // Drive Lenis from the GSAP ticker so both systems share one RAF loop.
    const rafCallback = (time: number) => {
      lenis.raf(time * 1000);
    };
    gsap.ticker.add(rafCallback);

    // Canonical Lenis + ScrollTrigger integration: push Lenis's smoothed
    // scroll value into ScrollTrigger on every scroll tick. Without this,
    // ScrollTrigger reads window.scrollY on its own RAF and ends up a frame
    // behind Lenis, which causes pinType: "transform" pins to vibrate.
    const onLenisScroll = () => ScrollTrigger.update();
    lenis.on('scroll', onLenisScroll);

    // Disable GSAP's lag-smoothing catchup so a dropped frame doesn't cause
    // a sudden jump on the next tick (which also manifests as jitter on
    // scrub-linked pinned timelines).
    gsap.ticker.lagSmoothing(0);

    lenis.start();

    // Deferred refresh: gives the browser one full frame after Lenis starts
    // to settle font metrics and subpixel layout before ScrollTrigger reads
    // the document height. Without this, Firefox reports a slightly-off
    // scrollHeight on initial mount (its font rendering settles one frame
    // later than Chrome's), and ScrollTrigger's trigger positions drift
    // proportionally — triggers near the top are fine, triggers near the
    // bottom are offset by several pixels. Chrome is unaffected (the
    // refresh is idempotent when nothing changed).
    const refreshRaf = requestAnimationFrame(() => {
      ScrollTrigger.refresh();
    });

    // That one-shot refresh only covers the initial page load — the Lenis
    // instance is created once and lives above the router, so it never re-runs
    // on an in-app navigation. Firefox needs it there too: when a client-side
    // navigation inserts a new subtree, it resolves container-query units
    // (100cqw, which is what sizes the service cards) against the viewport
    // instead of the query container for the first few hundred milliseconds.
    // The services page therefore mounts ~141px taller than its settled
    // layout, every ScrollTrigger on it is created against those wrong
    // heights, and nothing ever re-measures — so every trigger below the
    // first service section fires up to 170px early. Chrome resolves the
    // container in the first layout pass and is unaffected.
    //
    // Re-measure whenever the document height settles at a new value. The
    // post-refresh height is adopted as the new baseline so a refresh that
    // changes the height itself (pin spacing) cannot feed back into a loop.
    let lastHeight = docEl.scrollHeight;
    let isRefreshing = false;
    let settleTimer: ReturnType<typeof setTimeout> | undefined;

    const heightObserver = new ResizeObserver(() => {
      // refresh() reverts pins to measure them, which moves the height itself.
      if (isRefreshing) return;

      const height = docEl.scrollHeight;
      if (Math.abs(height - lastHeight) < 2) return;
      lastHeight = height;

      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        isRefreshing = true;
        ScrollTrigger.refresh();
        isRefreshing = false;
        lastHeight = docEl.scrollHeight;
      }, 150);
    });
    heightObserver.observe(docEl);

    return () => {
      cancelAnimationFrame(refreshRaf);
      clearTimeout(settleTimer);
      heightObserver.disconnect();
      lenis.off('scroll', onLenisScroll);
      gsap.ticker.remove(rafCallback);
      lenis.stop();
    };
  }, [lenis]);

  return null;
}
